import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  Clock,
  Users,
  CheckCircle,
  TrendingUp,
  Calendar,
  Activity,
  Target,
  RefreshCw,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from 'lucide-react';

export default function FacilitatorAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
    fetchRecentSessions();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/facilitator/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentSessions = async () => {
    try {
      const response = await axios.get('/api/facilitator/sessions?status=completed');
      setRecentSessions(response.data.sessions?.slice(0, 10) || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const TrendIndicator = ({ value, suffix = '%' }) => {
    if (value === 0) {
      return (
        <span className="flex items-center gap-1 text-muted-foreground text-sm">
          <Minus className="w-3 h-3" />
          No change
        </span>
      );
    }
    if (value > 0) {
      return (
        <span className="flex items-center gap-1 text-emerald-600 text-sm">
          <ArrowUpRight className="w-3 h-3" />
          +{value}{suffix}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-red-500 text-sm">
        <ArrowDownRight className="w-3 h-3" />
        {value}{suffix}
      </span>
    );
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hrs}h ${mins}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="facilitator-analytics" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            My Analytics
          </h1>
          <p className="text-muted-foreground">
            Track your facilitation performance and session metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]" data-testid="time-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Sessions */}
        <Card data-testid="kpi-total-sessions">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Total Sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">{analytics?.totalSessions || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">sessions facilitated</p>
              </div>
              <TrendIndicator value={0} />
            </div>
          </CardContent>
        </Card>

        {/* Completed Sessions */}
        <Card data-testid="kpi-completed-sessions">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Completed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">{analytics?.completedSessions || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">successfully completed</p>
              </div>
              <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                {analytics?.completionRate || 0}% rate
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Average Duration */}
        <Card data-testid="kpi-avg-duration">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              Avg. Duration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">{formatDuration(analytics?.avgDurationMinutes)}</p>
                <p className="text-xs text-muted-foreground mt-1">per session</p>
              </div>
              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                <Target className="w-3 h-3 mr-1" />
                On track
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Unique Participants */}
        <Card data-testid="kpi-unique-participants">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-500" />
              Participants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">{analytics?.uniqueParticipants || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">unique participants</p>
              </div>
              <TrendIndicator value={0} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Completion Rate Progress */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Session Completion Overview
            </CardTitle>
            <CardDescription>
              Your session completion rate compared to target
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Completion Rate</span>
                <span className="font-medium">{analytics?.completionRate || 0}%</span>
              </div>
              <Progress value={analytics?.completionRate || 0} className="h-3" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0%</span>
                <span className="text-emerald-600">Target: 95%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Session breakdown */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-2xl font-bold text-emerald-600">
                  {analytics?.completedSessions || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Completed</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">
                  {(analytics?.totalSessions || 0) - (analytics?.completedSessions || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">In Progress</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-2xl font-bold text-violet-600">
                  {analytics?.uniqueParticipants || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Participants</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              This Week
            </CardTitle>
            <CardDescription>
              Your recent activity summary
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-sm">Sessions This Week</span>
              </div>
              <span className="font-semibold">{recentSessions.length}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm">Avg. Session Time</span>
              </div>
              <span className="font-semibold">{formatDuration(analytics?.avgDurationMinutes)}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Users className="w-4 h-4 text-violet-600" />
                </div>
                <span className="text-sm">New Participants</span>
              </div>
              <span className="font-semibold">{analytics?.uniqueParticipants || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Recent Sessions
          </CardTitle>
          <CardDescription>
            Your latest completed facilitated sessions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentSessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No completed sessions yet</p>
              <p className="text-sm">Sessions you complete will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentSessions.map((session) => (
                <div 
                  key={session.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                  data-testid={`session-row-${session.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {session.participant?.firstName?.[0] || session.participant?.email?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">
                        {session.participant?.firstName 
                          ? `${session.participant.firstName} ${session.participant.lastName || ''}`
                          : session.participant?.email || 'Unknown'}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {session.type === 'fear' ? 'EDGE' : session.type === 'archetype' ? 'CORE' : session.type}
                        </Badge>
                        <span>•</span>
                        <span>{formatDate(session.completedAt || session.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">{session.duration || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">Duration</p>
                    </div>
                    <Badge 
                      variant={session.status === 'completed' ? 'default' : 'secondary'}
                      className={session.status === 'completed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                    >
                      {session.status === 'completed' ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Completed</>
                      ) : (
                        session.status
                      )}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
